import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDynamicContent } from "../lib/dynamicContent";

function getStatusText(isAvailable: boolean, isHovered: boolean, currentLang: string): string {
  const isTr = currentLang === "tr";
  if (!isAvailable) {
    return isTr ? "RANDEVU İÇİN MAİL AT" : "WRITE AN EMAIL TO BOOK";
  }
  return isTr ? "RANDEVU AL" : "CLICK TO BOOK";
}

function getTitleText(isAvailable: boolean, currentLang: string): string {
  const isTr = currentLang === "tr";
  if (isAvailable) {
    return isTr ? "Müsait - Takvim Randevusu Al" : "Available - Book a Session on Calendar";
  }
  return isTr ? "Dolu - E-posta Gönder" : "Fully Booked - Write an Email";
}

function useLiveClock() {
  const [time, setTime] = useState("");

  useEffect(() => {
    const updateTime = () => {
      const date = new Date();
      // Calculate UTC time and apply GMT+3 offset
      const utc = date.getTime() + date.getTimezoneOffset() * 60000;
      const localDate = new Date(utc + 3600000 * 3);
      setTime(
        localDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false
        })
      );
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  return time;
}

export function LiveAvailabilityFloatingButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, loading } = useDynamicContent();
  const time = useLiveClock();
  const [isHovered, setIsHovered] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentLang = pathParts[0] || "en";

  const isAvailable = settings.find((s) => s.key === "home_accepting_contracts")?.value !== "false";
  const calendarUrl = settings.find((s) => s.key === "home_calendar_url")?.value || "https://calendar.google.com/calendar/appointments/schedules/AcZssZ1TFvPkatsmeh5gSgRTbpyBF9B7gdYbycQfMENIodfpqdXWpdEyivnrE4vYz7UE8GzobuwFAoDH";

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setIsFooterVisible(entry.isIntersecting);
        }
      },
      {
        root: null,
        threshold: 0.05
      }
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
    };
  }, [location.pathname]); // Re-observe when navigation changes the DOM

  const handleAvailabilityClick = () => {
    if (isAvailable) {
      window.open(calendarUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/${currentLang}/contact`, {
        state: {
          subTab: "form",
          subject: "Availabilty - Uygunluk",
          showPopup: true
        }
      });
    }
  };


  if (loading) return null;

  const statusText = getStatusText(isAvailable, isHovered, currentLang);
  const titleText = getTitleText(isAvailable, currentLang);

  return (
    <button
      onClick={handleAvailabilityClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={titleText}
      className={`fixed bottom-6 right-6 z-50 flex items-center justify-center gap-3.5 px-5 py-3.5 rounded-2xl border-[5px] border-[#0472A3] text-white hover:scale-105 active:scale-95 hover:shadow-2xl hover:shadow-brand-primary/15 transition-all duration-300 shadow-xl select-none cursor-pointer group font-mono font-bold text-xs tracking-wide ${isFooterVisible ? "-translate-y-[88px] md:-translate-y-[56px]" : "translate-y-0"
        } ${isAvailable ? "bg-[#066E61]" : "bg-amber-600"}`}
    >
      {/* Pulsing indicator - green if available, amber if fully booked */}
      <span className="flex h-3 w-3 relative shrink-0">
        <span
          className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isAvailable ? "bg-[#00d973]" : "bg-amber-400"
            }`}
        />
        <span
          className={`relative inline-flex rounded-full h-3 w-3 ${isAvailable ? "bg-[#00d973]" : "bg-amber-500"
            }`}
        />
      </span>

      {/* Horizontal Status Text and GMT+3 Local Clock */}
      <span>
        {statusText} • {time} (GMT+3)
      </span>
    </button>
  );
}

export default LiveAvailabilityFloatingButton;
